import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import {
  type FilesHttpGetAssetsByAssetIdFilesQuery,
  httpGetAssetsByAssetIdFileSets,
  httpGetAssetsByAssetIdFiles,
  httpGetAssetsByAssetIdFormats,
  httpGetAssetsByAssetIdKeyframes,
  httpGetAssetsByAssetIdProxies,
  httpGetAssetsByAssetIdVersionsByVersionIdFileSets,
  httpGetAssetsByAssetIdVersionsByVersionIdFiles,
  httpGetAssetsByAssetIdVersionsByVersionIdFormats,
  type httpGetAssetsByAssetIdVersionsByVersionIdKeyframes,
  httpGetAssetsByAssetIdVersionsByVersionIdProxies,
} from '@iconik-io/iconik-api/src/services/files';

import { isPdfFormat } from '../../utils/is_pdf_format';
import { selectUserRoles } from '../auth/selectors';
import { type AppDispatch, createAppThunk } from '../types';

const perPage = 100;

// Cap on how many objects a single paginated fetch will load into memory.
// Assets can have many thousands of file sets (and other media data); loading
// them all crashed the tab. Callers can pass onLimitReached to warn the user
// that the results were truncated.
export const maxObjectsPerFetch = 1000;

type LastIdEndpointActionCreator =
  | typeof httpGetAssetsByAssetIdKeyframes
  | typeof httpGetAssetsByAssetIdFormats
  | typeof httpGetAssetsByAssetIdProxies
  | typeof httpGetAssetsByAssetIdFiles
  | typeof httpGetAssetsByAssetIdFileSets;

export async function fetchAllPagesByLastId<
  EndpointActionCreator extends LastIdEndpointActionCreator,
>(
  assetId: string,
  initialQuery: Parameters<EndpointActionCreator>[1],
  dispatch: AppDispatch,
  actionCreator: EndpointActionCreator,
  onLimitReached?: () => void,
) {
  const allObjects: EndpointResponse<
    ReturnType<EndpointActionCreator>
  >['body']['objects'] = [];
  let lastId: string | undefined;

  while (true) {
    const query = {
      ...initialQuery,
      per_page: perPage,
      last_id: lastId,
    };

    const response = await dispatch(actionCreator(assetId, query));

    const newObjects = response.body.objects;

    // biome-ignore lint/plugin: allObjects is a union of array types, so push() asks for their intersection, which reduces to never. newObjects comes from the same endpoint response and is the matching member of that union.
    allObjects.push(...(newObjects as any));
    if (allObjects.length >= maxObjectsPerFetch) {
      // Only warn when the collection is genuinely larger than what we loaded;
      // an asset with exactly maxObjectsPerFetch objects is not truncated.
      if (response.body.total > allObjects.length) {
        onLimitReached?.();
      }
      break;
    }
    if (newObjects.length === perPage) {
      lastId = newObjects[newObjects.length - 1].id;
    } else {
      break;
    }
  }

  return allObjects;
}

type LastIdEndpointVersionActionCreator =
  | typeof httpGetAssetsByAssetIdVersionsByVersionIdKeyframes
  | typeof httpGetAssetsByAssetIdVersionsByVersionIdFormats
  | typeof httpGetAssetsByAssetIdVersionsByVersionIdProxies
  | typeof httpGetAssetsByAssetIdVersionsByVersionIdFiles
  | typeof httpGetAssetsByAssetIdVersionsByVersionIdFileSets;

export async function fetchAllPagesVersionedByLastId<
  EndpointActionCreator extends LastIdEndpointVersionActionCreator,
>(
  assetId: string,
  versionId: string,
  initialQuery: Parameters<EndpointActionCreator>[2],
  dispatch: AppDispatch,
  actionCreator: EndpointActionCreator,
  onLimitReached?: () => void,
) {
  const allObjects: EndpointResponse<
    ReturnType<EndpointActionCreator>
  >['body']['objects'] = [];
  let lastId: string | undefined;

  while (true) {
    const query = {
      ...initialQuery,
      per_page: perPage,
      last_id: lastId,
    };

    const response = await dispatch(actionCreator(assetId, versionId, query));

    const newObjects = response.body.objects;

    // biome-ignore lint/plugin: allObjects is a union of array types, so push() asks for their intersection, which reduces to never. newObjects comes from the same endpoint response and is the matching member of that union.
    allObjects.push(...(newObjects as any));
    if (allObjects.length >= maxObjectsPerFetch) {
      // Only warn when the collection is genuinely larger than what we loaded;
      // an asset with exactly maxObjectsPerFetch objects is not truncated.
      if (response.body.total > allObjects.length) {
        onLimitReached?.();
      }
      break;
    }
    if (newObjects.length === perPage) {
      lastId = newObjects[newObjects.length - 1].id;
    } else {
      break;
    }
  }

  return allObjects;
}

type FetchAssetMediaDataOptions = {
  // Fetch media data only for a specified version.
  versionId?: string;

  // Files request doesn't support fetching all versions but is required for comparison mode.
  // In such cases we need to fetch all media data for all versions but for files we still
  // need to specify a certain version for which files will be fetched.
  filesVersionId?: string;

  // Reuse the previous fetch for this asset when it is younger than this many
  // milliseconds, instead of requesting keyframes, formats, proxies, files and
  // file sets again. Concurrent fetches for the same asset are shared. Leave it
  // unset to always fetch
  maxAge?: number;
};

type MediaDataFetch = {
  startedAt: number;
  promise: Promise<void>;
};

const mediaDataFetches = new Map<string, MediaDataFetch>();
const maxMediaDataFetches = 500;

// Entries past their window can never be reused, so drop them rather than hold
// them for the life of the tab. The size cap is a backstop for a session that
// somehow outpaces that: evicting an entry only costs one repeated fetch
function pruneMediaDataFetches(maxAge: number) {
  const now = Date.now();
  for (const [key, entry] of mediaDataFetches) {
    if (now - entry.startedAt >= maxAge) {
      mediaDataFetches.delete(key);
    }
  }
  while (mediaDataFetches.size >= maxMediaDataFetches) {
    const oldestKey = mediaDataFetches.keys().next().value;
    if (oldestKey === undefined) {
      return;
    }
    mediaDataFetches.delete(oldestKey);
  }
}

function getMediaDataFetchKey(
  assetId: string,
  options: FetchAssetMediaDataOptions,
  userId: string | undefined,
  userRoles: ReturnType<typeof selectUserRoles>,
) {
  return [
    userId ?? '',
    assetId,
    options.versionId ?? '',
    options.filesVersionId ?? '',
    userRoles.can_read_formats ? '1' : '0',
    userRoles.can_read_proxies ? '1' : '0',
    userRoles.can_read_files ? '1' : '0',
  ].join(':');
}

export const fetchAssetMediaData = createAppThunk(
  (assetId: string, options: FetchAssetMediaDataOptions = {}) =>
    async (dispatch, getState) => {
      const state = getState();
      const userRoles = selectUserRoles(state);

      if (options.maxAge === undefined) {
        return fetchMediaData(assetId, options, userRoles, dispatch);
      }

      const key = getMediaDataFetchKey(
        assetId,
        options,
        state.auth.currentUser?.id,
        userRoles,
      );
      const previous = mediaDataFetches.get(key);
      if (previous && Date.now() - previous.startedAt < options.maxAge) {
        return previous.promise;
      }

      pruneMediaDataFetches(options.maxAge);

      const promise = fetchMediaData(assetId, options, userRoles, dispatch);
      mediaDataFetches.set(key, { startedAt: Date.now(), promise });
      promise.catch(() => {
        if (mediaDataFetches.get(key)?.promise === promise) {
          mediaDataFetches.delete(key);
        }
      });

      return promise;
    },
);

async function fetchMediaData(
  assetId: string,
  options: FetchAssetMediaDataOptions,
  userRoles: ReturnType<typeof selectUserRoles>,
  dispatch: AppDispatch,
) {
  fetchAllPagesByLastId(
    assetId,
    { include_all_versions: true },
    dispatch,
    httpGetAssetsByAssetIdKeyframes,
  );

  // TODO(FE-7071): Remove the PDF detection and the forced signed URL
  // generation below once PDFs are rendered from proxies only and
  // originals are not loaded for viewing.
  let isPdf = false;
  if (userRoles.can_read_formats) {
    const formats = await (options.versionId
      ? fetchAllPagesVersionedByLastId(
          assetId,
          options.versionId,
          {},
          dispatch,
          httpGetAssetsByAssetIdVersionsByVersionIdFormats,
        )
      : fetchAllPagesByLastId(
          assetId,
          { include_all_versions: true },
          dispatch,
          httpGetAssetsByAssetIdFormats,
        ));
    isPdf = isPdfFormat(formats[0]?.metadata ?? undefined);
  }
  let hasClosedProxies = false;

  if (userRoles.can_read_proxies) {
    const proxies = await (options.versionId
      ? fetchAllPagesVersionedByLastId(
          assetId,
          options.versionId,
          {},
          dispatch,
          httpGetAssetsByAssetIdVersionsByVersionIdProxies,
        )
      : fetchAllPagesByLastId(
          assetId,
          { include_all_versions: true },
          dispatch,
          httpGetAssetsByAssetIdProxies,
        ));

    // We check if there are any proxies with status CLOSED to determine
    // if we need to generate signed URLs for files.
    // It's required for play original functionality during transcoding.
    hasClosedProxies = proxies.some((proxy) => proxy.status === 'CLOSED');
  }

  if (userRoles.can_read_files) {
    const query: FilesHttpGetAssetsByAssetIdFilesQuery = {
      generate_signed_url: !hasClosedProxies || isPdf ? true : undefined,
    };

    const filesVersionId = options.filesVersionId ?? options.versionId;

    await (filesVersionId
      ? fetchAllPagesVersionedByLastId(
          assetId,
          filesVersionId,
          query,
          dispatch,
          httpGetAssetsByAssetIdVersionsByVersionIdFiles,
        )
      : fetchAllPagesByLastId(
          assetId,
          query,
          dispatch,
          httpGetAssetsByAssetIdFiles,
        ));

    await (filesVersionId
      ? fetchAllPagesVersionedByLastId(
          assetId,
          filesVersionId,
          query,
          dispatch,
          httpGetAssetsByAssetIdVersionsByVersionIdFileSets,
        )
      : fetchAllPagesByLastId(
          assetId,
          query,
          dispatch,
          httpGetAssetsByAssetIdFileSets,
        ));
  }
}
